import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays } from "lucide-react";
import { apiGet } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { localCalendarDate } from "./planning-date";

interface WorkTask {
  id: string;
  title: string;
  dueDate: string | null;
  blockers?: { id: string; title: string }[];
}
interface WorkFacts {
  overdue: WorkTask[];
  dueToday: WorkTask[];
  blocked: WorkTask[];
  recommendedCandidates: WorkTask[];
  scheduledToday: {
    id: string;
    plannedMinutes: number | null;
    task: WorkTask;
  }[];
}

export function WorkPlanningPanel({
  projectId,
  onAsk,
  onTask,
}: {
  projectId: string;
  onAsk(text: string): void;
  onTask(id: string): void;
}) {
  const fr = useI18n().language === "fr";
  const [date, setDate] = useState(localCalendarDate);
  const [minutes, setMinutes] = useState(180);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const query = useQuery({
    queryKey: ["ai-work", projectId, date, timezone],
    queryFn: () =>
      apiGet<WorkFacts>(
        `/ai/projects/${projectId}/work?${new URLSearchParams({ date, timezone })}`,
      ),
  });
  return (
    <details
      className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm"
      data-testid="ai-planning"
    >
      <summary className="cursor-pointer flex items-center gap-2">
        <CalendarDays className="h-4 w-4" />
        {fr ? "Mon travail / Planification" : "My work / Planning"}
      </summary>
      <div className="space-y-3 mt-3">
        <div className="flex flex-wrap items-center gap-3">
          <label>
            {fr ? "Date" : "Date"}{" "}
            <input
              aria-label="Planning date"
              type="date"
              className="rounded p-1 bg-surface"
              value={date}
              onChange={(e) => {
                if (e.target.value) setDate(e.target.value);
              }}
            />
          </label>
          <label>
            {fr ? "Minutes disponibles" : "Available minutes"}{" "}
            <input
              aria-label="Available minutes"
              type="number"
              min={1}
              max={1440}
              className="w-20 rounded p-1 bg-surface"
              value={minutes}
              onChange={(e) =>
                setMinutes(Math.min(1440, Math.max(1, Number(e.target.value))))
              }
            />
          </label>
          <span className="text-xs text-foreground/70">{timezone}</span>
        </div>
        <p className="text-xs text-foreground/70">
          {fr
            ? "Une proposition de plan sera à valider. Les échéances restent distinctes."
            : "Review the proposed plan before applying it. Planned work and deadlines are separate."}
        </p>
        {query.error && (
          <p role="alert" className="text-destructive">
            {query.error.message}
          </p>
        )}
        {query.data && (
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              [fr ? "En retard" : "Overdue", query.data.overdue],
              [fr ? "À rendre ce jour" : "Due this day", query.data.dueToday],
              [fr ? "Blocages" : "Blocked", query.data.blocked],
              [
                fr ? "Déjà planifié" : "Already planned",
                query.data.scheduledToday.map((slot) => slot.task),
              ],
            ].map(([title, rows]) => (
              <div key={title as string}>
                <p className="font-medium">
                  {title as string} · {(rows as WorkTask[]).length}
                </p>
                {(rows as WorkTask[]).slice(0, 10).map((task) => (
                  <button
                    key={task.id}
                    className="block text-primary text-left py-1"
                    onClick={() => onTask(task.id)}
                  >
                    {task.title}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              onAsk(
                fr
                  ? `Prépare mon plan personnel pour le ${date} (${timezone}). J’ai ${minutes} minutes disponibles. Analyse les blocages et présente une proposition à valider.`
                  : `Prepare my personal plan for ${date} (${timezone}). I have ${minutes} available minutes. Check blockers and preview the plan for approval.`,
              )
            }
          >
            {fr ? "Planifier ma journée" : "Plan my day"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              onAsk(
                fr
                  ? `Prépare mon plan personnel pour les 7 jours à partir du ${date} (${timezone}), avec ${minutes} minutes par jour. Présente une proposition à valider.`
                  : `Prepare my personal plan for the 7 days starting ${date} (${timezone}), with ${minutes} minutes per day. Preview the plan for approval.`,
              )
            }
          >
            {fr ? "Planifier ma semaine" : "Plan my week"}
          </Button>
        </div>
      </div>
    </details>
  );
}
